/**
 * NTA 2026 Self-Service Card — Rent Relief + NHF Opt-Out
 *
 * Phase 9 Sprint 9-3: Grandma-simple self-service widgets for
 * Rent Relief declaration and NHF opt-out (NTA 2025 voluntary).
 *
 * Drop this into the portal page to give employees control over
 * their NTA 2026 benefits.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, CardContent, Typography, TextField, Button, Box, Switch,
  FormControlLabel, Alert, InputAdornment, Divider, Stack,
} from '@mui/material';
import {
  Home as HomeIcon,
  AccountBalance as NHFIcon,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import apiClient from '@/api/client';

interface Props {
  /** Current annual_rent_paid from employee record (null = not declared) */
  annualRentPaid: string | null;
  /** Current NHF opt-in status */
  nhfOptIn: boolean;
  /** Currency symbol */
  currencySymbol?: string;
}

export default function NTA2026SelfServiceCard({ annualRentPaid, nhfOptIn, currencySymbol = '₦' }: Props) {
  const qc = useQueryClient();
  const [rent, setRent] = useState(annualRentPaid ?? '');
  const [showRentSuccess, setShowRentSuccess] = useState(false);
  const [nhfStatus, setNhfStatus] = useState(nhfOptIn);
  const [nhfPending, setNhfPending] = useState(false);

  const rentMutation = useMutation({
    mutationFn: (annual_rent_paid: number) =>
      apiClient.post('/hr/self-service/rent-relief', { annual_rent_paid }),
    onSuccess: () => {
      setShowRentSuccess(true);
      qc.invalidateQueries({ queryKey: ['portal'] });
      setTimeout(() => setShowRentSuccess(false), 5000);
    },
  });

  const nhfMutation = useMutation({
    mutationFn: (optOut: boolean) =>
      apiClient.post('/hr/self-service/nhf-opt-out', { opt_out: optOut }),
    onSuccess: (data: any) => {
      if (data?.data?.pending_approval) {
        setNhfPending(true);
      } else {
        setNhfStatus(!nhfStatus);
      }
      qc.invalidateQueries({ queryKey: ['portal'] });
    },
  });

  const rentReliefMonthly = rent ? Math.min(Number(rent) * 0.2, 500000) / 12 : 0;

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={TYPE.semibold} gutterBottom>
          Tax Benefits
        </Typography>

        {/* Rent Relief */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <HomeIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={TYPE.medium}>
            Rent Relief Declaration
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
          How much rent do you pay per year? This reduces your tax automatically.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
          <TextField
            size="small"
            type="number"
            value={rent}
            onChange={e => setRent(e.target.value)}
            placeholder="e.g. 2,400,000"
            InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => rentMutation.mutate(Number(rent))}
            disabled={rentMutation.isPending || !rent}
          >
            {rentMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </Box>
        {showRentSuccess && (
          <Alert severity="success" sx={{ mb: 1 }}>
            Rent updated! Your monthly tax savings: {currencySymbol}{rentReliefMonthly.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </Alert>
        )}
        {Number(rent) > 0 && !showRentSuccess && (
          <Typography variant="caption" color="text.secondary">
            Estimated monthly relief: {currencySymbol}{rentReliefMonthly.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {/* NHF Opt-Out */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <NHFIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={TYPE.medium}>
            National Housing Fund (NHF)
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          NHF is now voluntary for private-sector employees. You can opt out to increase your take-home pay.
        </Typography>
        {nhfPending ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            Your opt-in request has been sent to your HR admin for approval.
          </Alert>
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={nhfStatus}
                onChange={() => nhfMutation.mutate(nhfStatus)} // toggle: if currently on, opt out; if off, request opt-in
                disabled={nhfMutation.isPending}
              />
            }
            label={nhfStatus ? 'Opted In (2.5% deducted)' : 'Opted Out'}
          />
        )}
      </CardContent>
    </Card>
  );
}
